// src/services/classReminders.js
// Firestore helpers for live class push notification reminders
// Collection: classReminders
// Doc ID: ${userId}_${classId} (composite — one reminder per user/class pair)
// Schema:
//   userId, classId, classTitle, scheduledAt (ISO), createdAt
//   notifiedTypes? — array of reminder types already delivered for this
//     user+class ('3h' and/or '15min'). Maintained via arrayUnion by both
//     the cron (api/send-class-reminder) and the immediate-send endpoint
//     (api/send-reminder-now). Replaces the legacy class-level
//     reminderSent_3h / reminderSent_15min flags on liveClasses, which
//     could not differentiate "this user has been notified" from "any
//     user has been notified" — the latter caused new reminders set
//     inside an active window to be permanently missed (Bug #3).
//
// When a user taps "Set reminder," we store their userId + classId.
// The send-class-reminder API (cron) queries this collection every 5
// minutes to find classes starting in ~3h or ~15min and sends push
// notifications. The send-reminder-now API fires the push immediately
// when a reminder is created INSIDE a notification window.

import { db, firebaseEnabled } from './firebase';
import {
    collection, doc, getDocs, query, where, serverTimestamp,
} from 'firebase/firestore';
// Writes wait for the member's Firestore identity (adr-firestore-identity.md a2).
import {
    addDoc, deleteDoc, setDoc,
} from './firestoreWrite';

// ── Set a reminder for a class ──
export async function setClassReminder(userId, classId, classTitle, scheduledAt) {
    if (!firebaseEnabled || !db || !userId || !classId) return false;
    try {
        const docId = `${userId}_${classId}`;
        await setDoc(doc(db, 'classReminders', docId), {
            userId,
            classId,
            classTitle: classTitle || '',
            scheduledAt: scheduledAt || '',
            createdAt: serverTimestamp(),
        });
        return true;
    } catch (e) { console.error('Error setting class reminder:', e); return false; }
}

// ── Remove a reminder for a class ──
export async function removeClassReminder(userId, classId) {
    if (!firebaseEnabled || !db || !userId || !classId) return false;
    try {
        const docId = `${userId}_${classId}`;
        await deleteDoc(doc(db, 'classReminders', docId));
        return true;
    } catch (e) { console.error('Error removing class reminder:', e); return false; }
}

// ── Check if a user has a reminder set for a class ──
export async function hasClassReminder(userId, classId) {
    if (!firebaseEnabled || !db || !userId || !classId) return false;
    try {
        const { getDoc } = await import('firebase/firestore');
        const snap = await getDoc(doc(db, 'classReminders', `${userId}_${classId}`));
        return snap.exists();
    } catch (e) { return false; }
}

// ── Get all reminders for a user (to show which classes have reminders set) ──
export async function getUserReminders(userId) {
    if (!firebaseEnabled || !db || !userId) return [];
    try {
        const q = query(collection(db, 'classReminders'), where('userId', '==', userId));
        const snap = await getDocs(q);
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e) { console.error('Error fetching user reminders:', e); return []; }
}
